import React, { useState, useRef, useEffect } from "react";
import { GrGroup } from "react-icons/gr";
import { isFulfilled } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import ReactDOM from "react-dom";

import useAppDispatch from "hooks/useAppDispatch";
import { updatePictureType } from "features/picture/pictureAction";
import { setErrorStatus } from "features/error/errorSlice";
import { PictureType } from "config/constants";
import type { Picture } from "types/Pictures";

interface ClassPictureButtonProps {
  picture: Picture;
}

const ClassPictureButton: React.FC<ClassPictureButtonProps> = ({ picture }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (showTooltip && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + rect.width / 2 + window.scrollX,
      });
    }
  }, [showTooltip]);

  const dispatch = useAppDispatch();
  const updateType = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const updateAction = await dispatch(
        updatePictureType({
          id: picture.id,
          formData: {
            picture_type:
              picture.picture_type === PictureType.class
                ? PictureType.non_ai
                : PictureType.class,
          },
        }),
      );
      if (!isFulfilled(updateAction)) {
        dispatch(setErrorStatus(""));
      }
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="relative inline-block z-40">
      <button
        ref={buttonRef}
        className={`flex items-center justify-center p-1 transition-colors duration-200 ease-out ${
          picture.picture_type !== PictureType.class
            ? "bg-white text-black hover:bg-teal-50 hover:text-black"
            : "bg-teal-500 text-white hover:bg-teal-600 hover:text-white"
        } rounded-md border border-[#B8B8B8] w-12 h-12 relative hover:border-teal-500 hover:shadow-sm`}
        onClick={updateType}
        disabled={isLoading}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-200 bg-opacity-70 rounded-md">
            <div className="w-5 h-5 border-2 border-t-teal-500 border-r-teal-500 border-b-gray-300 border-l-gray-300 rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <GrGroup className="text-2xl" />
            <span className="text-[10px] mt-1">
              {picture.picture_type === PictureType.class ? "ON" : "OFF"}
            </span>
          </div>
        )}
      </button>
      {showTooltip &&
        // TODO(portal-safety): Tooltip uses document.body for global positioning - intentionally kept as createPortal
        ReactDOM.createPortal(
          <div
            className="fixed z-[9999] w-max max-w-xs px-3 py-2 text-sm text-white bg-gray-800 rounded-lg shadow-lg"
            style={{
              top: `${tooltipPosition.top + 10}px`,
              left: `${tooltipPosition.left}px`,
              transform: "translateX(-50%)",
            }}
          >
            <div className="absolute w-3 h-3 transform -translate-x-1/2 rotate-45 bg-gray-800 -top-1 left-1/2"></div>
            <div className="relative z-20 whitespace-pre">
              {picture.picture_type === PictureType.class
                ? t("_unpublished_picture_class._class_picture_tooltip_on")
                : t("_unpublished_picture_class._class_picture_tooltip_off")}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ClassPictureButton;
